/**
 * Session work on this device: the downloaded work packs, results saved before they are sent, the
 * server's receipts and the chute screen's state. Stored in IndexedDB (its own database, separate
 * from the capture outbox) and sent through the idempotent sessions sync route.
 */
import { useEffect, useState, useSyncExternalStore } from "react";
import { ApiError, api } from "../../../web/lib/api";
import { invalidateAll } from "../../../web/lib/query";
import { MemoryKV, SendError, SessionOutbox, STORES, type KV, type Queued, type StoredReceipt, type Store } from "../shared/queue";
import type { Receipt, WorkPack } from "../shared/types";

const DB_NAME = "hondo-v2-sessions";

class IdbKV implements KV {
  private db: Promise<IDBDatabase>;
  constructor() {
    this.db = new Promise((resolve, reject) => {
      try {
        const req = indexedDB.open(DB_NAME, 1);
        req.onupgradeneeded = () => { for (const s of STORES) if (!req.result.objectStoreNames.contains(s)) req.result.createObjectStore(s); };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
        req.onblocked = () => reject(new Error("blocked"));
      } catch (err) { reject(err); }
    });
    this.db.catch(() => {});
  }
  private async req<T>(store: Store, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest): Promise<T> {
    const db = await this.db;
    return new Promise<T>((resolve, reject) => {
      const tx = db.transaction(store, mode);
      const r = fn(tx.objectStore(store));
      // Resolve once the transaction is durable, not merely queued.
      tx.oncomplete = () => resolve(r.result as T);
      tx.onerror = () => reject(tx.error ?? r.error);
      tx.onabort = () => reject(tx.error ?? new Error("aborted"));
    });
  }
  get<T>(store: Store, key: string) { return this.req<T | undefined>(store, "readonly", (s) => s.get(key)); }
  async put<T>(store: Store, key: string, value: T) { await this.req(store, "readwrite", (s) => s.put(value, key)); }
  async del(store: Store, key: string) { await this.req(store, "readwrite", (s) => s.delete(key)); }
  all<T>(store: Store) { return this.req<T[]>(store, "readonly", (s) => s.getAll()); }
}

function makeStorage(): KV {
  try { if (typeof indexedDB !== "undefined") return new IdbKV(); } catch { /* fall through */ }
  return new MemoryKV();
}

export const outbox = new SessionOutbox(makeStorage());

// ---------------------------------------------------------------- live views for React

let version = 0;
outbox.subscribe(() => { version++; });
const subscribe = (l: () => void) => outbox.subscribe(l);

/** Re-run an async read whenever the outbox changes. */
function useOutboxQuery<T>(read: () => Promise<T>, initial: T, deps: unknown[] = []): T {
  const v = useSyncExternalStore(subscribe, () => version, () => 0);
  const [value, setValue] = useState<T>(initial);
  useEffect(() => {
    let alive = true;
    read().then((x) => { if (alive) setValue(x); }).catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [v, outbox.owner, ...deps]);
  return value;
}

export function useQueued(jobId?: string): Queued[] {
  return useOutboxQuery(() => outbox.list(jobId), [], [jobId]);
}

export function useReceipts(jobId?: string): StoredReceipt[] {
  return useOutboxQuery(() => outbox.receipts(jobId), [], [jobId]);
}

export function useStoredPacks() {
  return useOutboxQuery(() => outbox.packs(), [], []);
}

// ---------------------------------------------------------------- connectivity and sending

const online = { value: typeof navigator === "undefined" ? true : navigator.onLine };
const onlineListeners = new Set<() => void>();
export function useOnline(): boolean {
  return useSyncExternalStore((l) => { onlineListeners.add(l); return () => onlineListeners.delete(l); }, () => online.value, () => true);
}

export interface SyncState { sending: boolean; lastError: string | null; lastStop: "offline" | "auth" | null; lastAt: number | null }
let syncState: SyncState = { sending: false, lastError: null, lastStop: null, lastAt: null };
const syncListeners = new Set<() => void>();
const setSync = (p: Partial<SyncState>) => { syncState = { ...syncState, ...p }; syncListeners.forEach((l) => l()); };
export function useSyncState(): SyncState {
  return useSyncExternalStore((l) => { syncListeners.add(l); return () => syncListeners.delete(l); }, () => syncState, () => syncState);
}

async function send(jobId: string, batch: Parameters<Parameters<SessionOutbox["flush"]>[0]>[1]): Promise<{ receipts: Receipt[] }> {
  try {
    return await api.post<{ receipts: Receipt[] }>(`/m/sessions/${jobId}/sync`, batch);
  } catch (err) {
    if (err instanceof ApiError) throw new SendError(err.status, err.message);
    throw new SendError(0, err instanceof Error ? err.message : String(err));
  }
}

/** Send what's waiting. Safe to call any time; does nothing without an owner or a connection. */
export async function flushNow(): Promise<void> {
  if (!outbox.owner) return;
  setSync({ sending: true });
  try {
    const r = await outbox.flush(send);
    setSync({ sending: false, lastStop: r.stopped, lastError: null, lastAt: Date.now() });
    if (r.sent) await invalidateAll();
  } catch (err) {
    setSync({ sending: false, lastError: err instanceof Error ? err.message : String(err), lastAt: Date.now() });
  }
}

/** Scope the outbox to the signed-in ranch and person, and drop cached ranch data if access ended. */
export function startSessionOutbox(ranchId: string, memberId: string, canRead: boolean): void {
  const owner = `${ranchId}:${memberId}`;
  if (outbox.owner === owner) return;
  outbox.setOwner(owner);
  void outbox.purgeOtherOwners();
  if (!canRead) void outbox.purgeCaches();
  void flushNow();
}

if (typeof window !== "undefined") {
  window.addEventListener("online", () => { online.value = true; onlineListeners.forEach((l) => l()); void flushNow(); });
  window.addEventListener("offline", () => { online.value = false; onlineListeners.forEach((l) => l()); });
  setInterval(() => { if (online.value && outbox.owner) void outbox.list().then((q) => { if (q.some((x) => x.state === "saved")) void flushNow(); }); }, 20_000);
}

// ---------------------------------------------------------------- work packs

export type PackStatus = "loading" | "ready" | "missing" | "error";

/** The session's work pack: from this device first, refreshed from the server when there's signal. */
export function usePack(jobId: string | undefined): { pack: WorkPack | null; status: PackStatus; refreshing: boolean; refresh: () => Promise<void>; error: string | null } {
  const [pack, setPack] = useState<WorkPack | null>(null);
  const [status, setStatus] = useState<PackStatus>("loading");
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const owner = useSyncExternalStore(subscribe, () => outbox.owner, () => "");
  const refresh = async () => {
    if (!jobId || !outbox.owner) return;
    setRefreshing(true);
    try {
      const before = Date.now();
      const fresh = await api.get<WorkPack>(`/m/sessions/${jobId}/pack`);
      const after = Date.now();
      const skew = Date.parse(fresh.server_time) - Math.round((before + after) / 2);
      const stored = { ...fresh, downloaded_at: new Date(after).toISOString(), clock_skew_ms: skew };
      await outbox.savePack(stored);
      setPack(stored);
      setStatus("ready");
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setStatus((s) => (s === "ready" ? s : pack ? "ready" : err instanceof ApiError && err.status !== 0 ? "error" : "missing"));
    } finally {
      setRefreshing(false);
    }
  };
  useEffect(() => {
    if (!jobId || !owner) return;
    let alive = true;
    outbox.pack(jobId).then((p) => {
      if (!alive) return;
      if (p) { setPack(p); setStatus("ready"); }
      void refresh();
    });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, owner]);
  return { pack, status, refreshing, refresh, error };
}

/** Download a session for offline use without opening it (from the job page or the list). */
export async function downloadPack(jobId: string): Promise<WorkPack> {
  const before = Date.now();
  const fresh = await api.get<WorkPack>(`/m/sessions/${jobId}/pack`);
  const after = Date.now();
  const stored = { ...fresh, downloaded_at: new Date(after).toISOString(), clock_skew_ms: Date.parse(fresh.server_time) - Math.round((before + after) / 2) };
  await outbox.savePack(stored);
  return stored;
}
